import { createAsyncThunk } from '@reduxjs/toolkit';
import { urls } from '@gift/common/_urls';
import { AppThunkApi, AppThunkApiConfig, WebsiteState } from '../store';

type AuthorizeResult = {
    jwt: string,
    privateId: string
};

export const authorizeNew = createAsyncThunk<AuthorizeResult, void, AppThunkApiConfig>(
    'project/authorizeNew',
    async (_, thunkApi) => {
        const request = formatRequestForNewProject(thunkApi.getState());
        const response = await fetch(urls.requestNewToken, request);
        if (response.ok) {
            return await response.json() as AuthorizeResult;
        } else {
            throw new Error(`Auth error: ${urls.requestNewToken} [${response.status}]`);
        }
    }
);

export const authorizeExisting = createAsyncThunk<AuthorizeResult, void, AppThunkApiConfig>(
    'project/authorizeExisting',
    async (_, thunkApi) => {
        const request = formatRequestForExistingProject(thunkApi.getState());
        const response = await fetch(urls.requestExistingToken, request);
        if (response.ok) {
            return await response.json() as AuthorizeResult;
        } else if (response.status === 401) {
            throw new Error('Auth error: challenge is not implemented yet [401]');
        } else {
            throw new Error(`Auth error: ${urls.requestExistingToken} [${response.status}]`);
        }
    }
);

function formatRequestForNewProject(state: WebsiteState) {
    return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            tenantId: state.tenant.id
        })
    };
}

function formatRequestForExistingProject(state: WebsiteState) {
    return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            privateId: state.project.privateId
        })
    };
}

export async function withAuthorization(
    request: (state: WebsiteState) => Promise<Response>,
    thunkApi: AppThunkApi
) {
    await authorizeIfNewProject(thunkApi);
    const response = await request(thunkApi.getState());
    if (response.status === 401) {
        return await tryAuthorizeThenRetry(request, thunkApi);
    } else {
        return response;
    }
}

async function authorizeIfNewProject(thunkApi: AppThunkApi) {
    if (!thunkApi.getState().project.privateId) {
        await thunkApi.dispatch(authorizeNew());
    }
}

async function tryAuthorizeThenRetry(
    request: (state: WebsiteState) => Promise<Response>,
    thunkApi: AppThunkApi
) {
    const result = await thunkApi.dispatch(authorizeExisting());
    if (result.meta.requestStatus === 'fulfilled') {
        return await request(thunkApi.getState());
    } else {
        throw new Error('Failed to obtain authorization.');
    }
}